import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import type { DuelOpponent, Exercise, DuelType } from './types';
import { DURATIONS, CATEGORIES } from './types';

interface Props {
  colors: Record<string, string>;
  selectedOpponent: DuelOpponent | null;
  duelType: DuelType | null;
  selectedExercise: Exercise | null;
  selectedCategory: string | null;
  duration: number;
  loading: boolean;
  onDurationChange: (hours: number) => void;
  onSubmit: () => void;
  onBack: () => void;
}

export default function ReviewStep({
  colors: c, selectedOpponent, duelType, selectedExercise,
  selectedCategory, duration, loading,
  onDurationChange, onSubmit, onBack,
}: Props) {
  return (
    <View style={styles.stepContent}>
      <Text style={[styles.stepTitle, { color: c.text }]}>Review & Send</Text>

      <View style={[styles.reviewCard, { backgroundColor: c.surface, borderColor: c.border }]}>
        <View style={styles.reviewRow}>
          <Text style={[styles.reviewLabel, { color: c.textSecondary }]}>Opponent:</Text>
          <Text style={[styles.reviewValue, { color: c.text }]}>
            {selectedOpponent?.display_name}
          </Text>
        </View>

        <View style={styles.reviewRow}>
          <Text style={[styles.reviewLabel, { color: c.textSecondary }]}>Type:</Text>
          <Text style={[styles.reviewValue, { color: c.text }]}>
            {duelType === 'pr' ? 'Strongest Lift' : duelType === '1rm' ? '1RM Challenge' : duelType === 'consistency' ? 'Consistency Check' : 'Volume War'}
          </Text>
        </View>

        {duelType !== 'consistency' && (
        <View style={styles.reviewRow}>
          <Text style={[styles.reviewLabel, { color: c.textSecondary }]}>
            {(duelType === 'pr' || duelType === '1rm') ? 'Exercise:' : 'Category:'}
          </Text>
          <Text style={[styles.reviewValue, { color: c.text }]}>
            {(duelType === 'pr' || duelType === '1rm')
              ? selectedExercise?.name
              : CATEGORIES.find((cat) => cat.id === selectedCategory)?.name}
          </Text>
        </View>
        )}

        <View style={styles.reviewRow}>
          <Text style={[styles.reviewLabel, { color: c.textSecondary }]}>Duration:</Text>
          <View style={styles.durationChips}>
            {DURATIONS.map((d) => (
              <TouchableOpacity
                key={d.hours}
                style={[
                  styles.durationChip,
                  {
                    backgroundColor: duration === d.hours ? c.accent : c.surfaceVariant,
                    borderColor: c.border,
                  },
                ]}
                onPress={() => onDurationChange(d.hours)}>
                <Text
                  style={[
                    styles.durationChipText,
                    { color: duration === d.hours ? c.text : c.textSecondary },
                  ]}>
                  {d.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      <TouchableOpacity
        style={[styles.sendButton, { backgroundColor: c.accent }]}
        onPress={onSubmit}
        disabled={loading}>
        {loading ? (
          <ActivityIndicator color={c.text} />
        ) : (
          <Text style={[styles.sendButtonText, { color: c.text }]}>Send Challenge</Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={[styles.backButton, { backgroundColor: c.surfaceVariant }]}
        onPress={onBack}>
        <Text style={[styles.backButtonText, { color: c.text }]}>Back</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  stepContent: { flex: 1, padding: 20 },
  stepTitle: { fontSize: 20, fontWeight: '800', marginBottom: 16 },
  reviewCard: { borderWidth: 1, borderRadius: 2, padding: 16, gap: 12, marginBottom: 20 },
  reviewRow: { gap: 4 },
  reviewLabel: { fontSize: 13 },
  reviewValue: { fontSize: 16, fontWeight: '700' },
  durationChips: { flexDirection: 'row', gap: 8, marginTop: 4 },
  durationChip: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 4, borderWidth: 1 },
  durationChipText: { fontSize: 13, fontWeight: '600' },
  sendButton: { padding: 16, borderRadius: 0, alignItems: 'center' },
  sendButtonText: { fontSize: 16, fontWeight: '800' },
  backButton: { padding: 14, borderRadius: 0, alignItems: 'center', marginTop: 8 },
  backButtonText: { fontSize: 15, fontWeight: '600' },
});
